import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Printer, FileText, RefreshCw } from "lucide-react";
import { PageHeader, PageShell } from "@/components/PageHeader";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("aa_ops_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

/**
 * <ReportShell>
 * Shared reporting UI. Consumers provide:
 *   - reportKey (matches backend route: /api/reports/{reportKey})
 *   - title
 *   - filters: [{ key, label, type: "date"|"text"|"select"|"boolean", options?, defaultValue? }]
 *   - defaultFilters (initial state, optional)
 *
 * The shell fetches on Run and renders `columns` + `rows` from the response.
 * Every report gets CSV + PDF + Print for free.
 */
export const ReportShell = ({ reportKey, title, subtitle, filters = [], defaultFilters = {}, autoRun = true }) => {
  const [values, setValues] = useState(() => ({
    ...Object.fromEntries(filters.map((f) => [f.key, f.defaultValue ?? ""])),
    ...defaultFilters,
  }));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);

  const params = useMemo(() => {
    const p = {};
    Object.entries(values).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p[k] = v;
    });
    return p;
  }, [values]);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/${reportKey}`, { params });
      setData(r.data);
    } catch (e) {
      toast.error("Report could not be generated. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoRun) run(); /* eslint-disable-next-line */
  }, []);

  const downloadUrl = (fmt) => {
    const qs = new URLSearchParams({ ...params, format: fmt }).toString();
    return `${BACKEND}/api/reports/${reportKey}?${qs}`;
  };

  const doDownload = async (fmt) => {
    try {
      const r = await api.get(`/reports/${reportKey}`, {
        params: { ...params, format: fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportKey}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to download ${fmt.toUpperCase()}`);
    }
  };

  const rowsSorted = useMemo(() => {
    if (!data?.rows || !sortKey) return data?.rows || [];
    const copy = [...data.rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sortDir;
      return String(av).localeCompare(String(bv)) * sortDir;
    });
    return copy;
  }, [data, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(-sortDir);
    else { setSortKey(k); setSortDir(1); }
  };

  return (
    <PageShell>
      <PageHeader
        testid={`page-report-${reportKey}`}
        title={<span className="inline-flex items-center gap-2"><FileText size={20} className="text-coach-blue" />{title}</span>}
        subtitle={subtitle}
        right={
          <div className="flex items-center gap-1.5">
            <button data-testid={`report-${reportKey}-run`} onClick={run} disabled={loading}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-sm text-[12.5px] font-medium bg-coach-blue text-white hover:bg-deep-road-navy disabled:opacity-60">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />{loading ? "Running…" : "Run"}
            </button>
            <button data-testid={`report-${reportKey}-csv`} onClick={() => doDownload("csv")} className="inline-flex items-center gap-1 h-9 px-2.5 rounded-sm text-[12.5px] font-medium text-coach-blue border border-coach-silver hover:bg-chrome-mist">
              <Download size={13} /> CSV
            </button>
            <button data-testid={`report-${reportKey}-pdf`} onClick={() => doDownload("pdf")} className="inline-flex items-center gap-1 h-9 px-2.5 rounded-sm text-[12.5px] font-medium text-coach-blue border border-coach-silver hover:bg-chrome-mist">
              <FileText size={13} /> PDF
            </button>
            <button data-testid={`report-${reportKey}-print`} onClick={() => window.print()} className="inline-flex items-center gap-1 h-9 px-2.5 rounded-sm text-[12.5px] font-medium text-coach-blue border border-coach-silver hover:bg-chrome-mist">
              <Printer size={13} /> Print
            </button>
          </div>
        }
      />
      {filters.length > 0 && (
        <div className="bg-white border border-coach-silver rounded-md p-3 mb-3 flex flex-wrap gap-3 no-print" data-testid={`report-${reportKey}-filters`}>
          {filters.map((f) => (
            <label key={f.key} className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">{f.label}</div>
              {f.type === "select" ? (
                <select data-testid={`report-${reportKey}-f-${f.key}`}
                  value={values[f.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  className="border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white min-w-[140px]">
                  <option value="">— Any —</option>
                  {(f.options || []).map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
                </select>
              ) : f.type === "boolean" ? (
                <select data-testid={`report-${reportKey}-f-${f.key}`}
                  value={values[f.key] === true ? "true" : values[f.key] === false ? "false" : ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value === "" ? "" : e.target.value === "true" }))}
                  className="border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white min-w-[100px]">
                  <option value="">— Any —</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : (
                <input data-testid={`report-${reportKey}-f-${f.key}`}
                  type={f.type || "text"}
                  value={values[f.key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  className="border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] min-w-[140px]" />
              )}
            </label>
          ))}
        </div>
      )}
      {loading && <div className="text-[13px] text-steel py-6 text-center">Loading…</div>}
      {!loading && data && (data.rows?.length === 0 ? (
        <div className="bg-white border border-coach-silver rounded-md p-8 text-center text-[13px] text-steel" data-testid={`report-${reportKey}-empty`}>
          No results for the selected filters.
        </div>
      ) : (
        <div className="bg-white border border-coach-silver rounded-md overflow-x-auto" data-testid={`report-${reportKey}-table`}>
          <table className="w-full text-[12.5px]">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel sticky top-0">
              <tr>
                {(data.columns || []).map((c) => (
                  <th key={c.key} className="text-left px-3 py-2 cursor-pointer whitespace-nowrap" onClick={() => toggleSort(c.key)}>
                    {c.label}{sortKey === c.key && <span> {sortDir === 1 ? "↑" : "↓"}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowsSorted.map((r, i) => (
                <tr key={i} className="border-t border-coach-silver/60 hover:bg-road-white">
                  {(data.columns || []).map((c) => (
                    <td key={c.key} className="px-3 py-1.5 text-carbon whitespace-nowrap">
                      {typeof r[c.key] === "boolean" ? (r[c.key] ? "Yes" : "No") : (r[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {data.totals && (
            <div className="px-3 py-2 border-t border-coach-silver/60 text-[11.5px] text-steel flex flex-wrap gap-4">
              {Object.entries(data.totals).map(([k, v]) => (
                <span key={k}><strong className="text-carbon">{k.replace(/_/g, " ")}:</strong> {String(v)}</span>
              ))}
            </div>
          )}
        </div>
      ))}
      {data?.unresolved_terms?.length > 0 && (
        <div className="mt-3 text-[11px] text-amber-hold" data-testid={`report-${reportKey}-unresolved`}>
          <strong>Unresolved legacy terms:</strong>
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            {data.unresolved_terms.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}
      {data?.notes?.length > 0 && (
        <div className="mt-2 text-[11px] text-steel">
          <ul className="list-disc ml-5 space-y-0.5">
            {data.notes.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          [data-testid^="page-report-"] { padding: 0 !important; }
          table thead { display: table-header-group; }
          nav, aside, header, .aa-nav-bg { display: none !important; }
        }
      `}</style>
    </PageShell>
  );
};

export const CONTACT_ROLES = [
  "artist_principal", "tour_manager", "production_manager", "business_manager",
  "personal_manager", "road_manager", "transportation_coordinator", "accountant",
  "accounts_payable", "booking_agent", "manager", "assistant", "security",
  "vendor_contact", "pickup_dropoff", "other",
];
